import { useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { AnalyticsBreakdown, AnalyticsUsageApp, AnalyticsUsageCountry, AnalyticsUsageScreen } from '@/api/client'
import { cn } from '@/lib/utils'
import { AppIcon, Button, DataTable, EmptyState, FilterSelect, Skeleton, useIsDesktop, type Column } from '@/ui'
import { fmtDuration, fmtNum, fmtPer } from './format'
import { Section } from './Section'
import { SortLabel, type SortState, useTableSort } from './sort'

/** The sortable columns. `time` reads a different field per level — an app and
 *  a country are timed by session, a screen by dwell — so the sort is keyed by
 *  what the column MEANS rather than by a field name. */
type SortKey = 'name' | 'users' | 'per' | 'views' | 'time'
type Sort = SortState<SortKey>

const TEXT_KEYS: readonly SortKey[] = ['name']
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'time', label: '평균 시간' },
  { value: 'views', label: '조회수' },
  { value: 'users', label: '활성 사용자' },
  { value: 'per', label: '사용자당 조회수' },
  { value: 'name', label: '앱 이름' },
]

/** The screen table, in the shape of the GA4 "pages and screens" report.
 *
 *  Three levels: the app, the countries it reached, and the screens opened in
 *  each. The app comes first because that is the unit the portfolio is managed
 *  in — keyed by country first, one product scatters down the table as its
 *  markets sort apart and "how is this app doing" has no row to read.
 *
 *  Active users are counted at every level rather than summed from the level
 *  below: one person who opens three screens is one active user, and one who
 *  travels is one user of the app and two country rows. Time is the mean
 *  session on an app or country row and the mean measured dwell on a screen
 *  row — how long the visit lasted versus how long that screen was on display.
 *
 *  Wide: one table, three levels of rows. Phone: a card per app, with its
 *  countries and screens folded under it. */
export function ScreenBreakdown({ data, loading }: { data?: AnalyticsBreakdown; loading?: boolean }) {
  const desktop = useIsDesktop()
  // Longest first, because the question this table opens with is "what held
  // anyone's attention", and a screen nobody stayed on is the least
  // interesting row on it.
  const { sort, pick } = useTableSort<SortKey>({ key: 'time', dir: 'desc' }, TEXT_KEYS)
  const apps = useMemo(() => sortApps(data?.apps ?? [], sort), [data, sort])

  return (
    <Section
      title="앱·국가별 화면"
      aside="앱, 그 앱이 닿은 국가, 국가에서 연 화면 순"
      actions={
        !desktop && (
          <FilterSelect
            label="화면 표 정렬"
            value={sort.key}
            onChange={(v) => pick(v as SortKey)}
            options={SORT_OPTIONS.map((o) => ({ value: o.value, label: `정렬 · ${o.label}` }))}
          />
        )
      }
    >
      {loading && !data ? (
        <Skeleton className="h-40 w-full" />
      ) : apps.length === 0 ? (
        <EmptyState title="이 기간에 화면 조회가 없다" />
      ) : desktop ? (
        <WideTable data={data!} apps={apps} sort={sort} pick={pick} />
      ) : (
        <PhoneCards apps={apps} sort={sort} />
      )}
    </Section>
  )
}

function WideTable({ data, apps, sort, pick }: { data: AnalyticsBreakdown; apps: AnalyticsUsageApp[]; sort: Sort; pick: (k: SortKey) => void }) {
  // One scale per column across the whole table, so a bar means the same thing
  // on every row. Scaled per group it would make the busiest screen of a dead
  // app look like the busiest screen of a live one.
  const every = apps.flatMap((a) => [a, ...countriesOf(a).flatMap((c) => [c, ...screensOf(c)])])
  const scale = {
    users: Math.max(1, ...every.map((r) => r.users)),
    views: Math.max(1, ...every.map((r) => r.views)),
    per: Math.max(1, ...every.map((r) => r.views_per_user)),
  }
  const sortTh = (k: SortKey, label: string, right?: boolean) => (
    <Th className={right ? 'text-right' : undefined}>
      <SortLabel k={k} sort={sort} onPick={pick} title="눌러서 정렬. 표의 모든 단계가 이 열을 따른다.">
        {label}
      </SortLabel>
    </Th>
  )
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full min-w-[880px] border-collapse text-table" aria-label="앱·국가별 화면">
        <thead>
          <tr className="border-b border-border text-meta text-muted-foreground">
            <Th className="w-10 text-right">#</Th>
            {sortTh('name', '앱')}
            <Th>국가</Th>
            <Th>화면</Th>
            <Th>앱 버전</Th>
            {sortTh('users', '활성 사용자', true)}
            {sortTh('per', '사용자당 조회수', true)}
            {sortTh('views', '조회수', true)}
            {sortTh('time', '평균 시간', true)}
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-border bg-muted/40 font-medium">
            <Td />
            <Td>합계</Td>
            <Td />
            <Td />
            <Td />
            <Td className="text-right tabular-nums">{fmtNum(data.users)}</Td>
            <Td className="text-right tabular-nums">{fmtPer(data.views_per_user)}</Td>
            <Td className="text-right tabular-nums">{fmtNum(data.views)}</Td>
            <Td className="text-right tabular-nums">{fmtDuration(data.avg_session_ms)}</Td>
          </tr>
          {apps.map((app, i) => (
            <AppRows key={app.app_id} index={i + 1} app={app} sort={sort} scale={scale} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

type Scale = { users: number; views: number; per: number }

function AppRows({ index, app, sort, scale }: { index: number; app: AnalyticsUsageApp; sort: Sort; scale: Scale }) {
  // Every level follows the column the header sorted by, so one click
  // reorders the whole table rather than only its top.
  const countries = sortCountries(countriesOf(app), sort)
  return (
    <>
      <tr className="border-b border-border/60 bg-muted/25">
        <Td className="text-right text-meta text-muted-foreground">{index}</Td>
        <Td className="font-medium">
          <span className="flex items-center gap-2">
            <AppIcon src={app.linked?.icon_url} name={app.linked?.name ?? app.app_id} size={20} />
            <span className="truncate">{app.linked?.name ?? app.app_id}</span>
          </span>
        </Td>
        <Td />
        <Td />
        <Td className="text-meta text-muted-foreground">{app.versions || '—'}</Td>
        <BarCell value={app.users} max={scale.users} format={fmtNum} />
        <BarCell value={app.views_per_user} max={scale.per} format={fmtPer} />
        <BarCell value={app.views} max={scale.views} format={fmtNum} />
        <Td className="text-right tabular-nums">{fmtDuration(app.avg_session_ms)}</Td>
      </tr>
      {countries.map((c) => (
        <CountryRows key={c.country} country={c} sort={sort} scale={scale} />
      ))}
    </>
  )
}

/** A country lists this many screens before "더 보기": a busy app reaches
 *  dozens of screens in each of a dozen countries. */
const SCREEN_LIMIT = 5

function CountryRows({ country, sort, scale }: { country: AnalyticsUsageCountry; sort: Sort; scale: Scale }) {
  const [all, setAll] = useState(false)
  const sorted = sortScreens(screensOf(country), sort)
  const screens = all ? sorted : sorted.slice(0, SCREEN_LIMIT)
  const hidden = sorted.length - SCREEN_LIMIT
  return (
    <>
      <tr className="border-b border-border/40">
        <Td />
        <Td />
        <Td className="pl-3">{countryName(country.country)}</Td>
        <Td />
        <Td />
        <BarCell value={country.users} max={scale.users} format={fmtNum} />
        <BarCell value={country.views_per_user} max={scale.per} format={fmtPer} />
        <BarCell value={country.views} max={scale.views} format={fmtNum} />
        <Td className="text-right tabular-nums">{fmtDuration(country.avg_session_ms)}</Td>
      </tr>
      {screens.map((s) => (
        <tr key={`${s.screen}|${s.app_version}`} className="border-b border-border/30">
          <Td />
          <Td />
          <Td />
          {/* Indented rather than repeating the app and country on every row:
              the rows above already said them, and repeating them makes the
              screen — what this row is about — harder to find. */}
          <Td className="pl-6">{s.screen || '(지정 안 됨)'}</Td>
          <Td className="text-meta text-muted-foreground">{s.app_version || '—'}</Td>
          <BarCell value={s.users} max={scale.users} format={fmtNum} />
          <BarCell value={s.views_per_user} max={scale.per} format={fmtPer} />
          <BarCell value={s.views} max={scale.views} format={fmtNum} />
          <Td className="text-right tabular-nums">{fmtDuration(s.avg_dwell_ms)}</Td>
        </tr>
      ))}
      {hidden > 0 && (
        <tr className="border-b border-border/30">
          <Td colSpan={3} />
          <Td colSpan={6} className="pl-6">
            <button type="button" onClick={() => setAll((v) => !v)} className="text-meta text-accent hover:underline">
              {all ? '접기' : `화면 ${hidden}개 더 보기`}
            </button>
          </Td>
        </tr>
      )}
    </>
  )
}

/** A number with a bar behind it. The bar is a background, not a column, so the
 *  figure stays readable and the eye can still scan magnitudes down the
 *  column. Zero draws nothing — a hairline at zero reads as a small value
 *  rather than none. */
function BarCell({ value, max, format }: { value: number; max: number; format: (n: number) => string }) {
  const pct = max > 0 && value > 0 ? Math.max(2, (value / max) * 100) : 0
  return (
    <td className="relative px-3 py-2 text-right tabular-nums">
      {pct > 0 && <span aria-hidden className="absolute inset-y-1 right-0 rounded-sm bg-chart-1/15" style={{ width: `${pct}%` }} />}
      <span className="relative">{format(value)}</span>
    </td>
  )
}

function PhoneCards({ apps, sort }: { apps: AnalyticsUsageApp[]; sort: Sort }) {
  const [open, setOpen] = useState<Set<string>>(new Set())
  const toggle = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur)
      if (!next.delete(id)) next.add(id)
      return next
    })
  const columns: Column<AnalyticsUsageApp>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (a) => (
        <span className="flex min-w-0 items-center gap-2">
          <AppIcon src={a.linked?.icon_url} name={a.linked?.name ?? a.app_id} size={24} />
          <span className="truncate">{a.linked?.name ?? a.app_id}</span>
        </span>
      ),
    },
    { key: 'versions', header: '앱 버전', card: 'line', cardHide: (a) => !a.versions, cell: (a) => a.versions },
    // the four numbers in two lines, each pair read together
    {
      key: 'reach',
      header: '사용자 · 조회수',
      card: 'line',
      cell: (a) => (
        <span className="tabular-nums">
          {fmtNum(a.users)} <span className="text-muted-foreground">·</span> {fmtNum(a.views)}
        </span>
      ),
    },
    {
      key: 'depth',
      header: '1인 조회 · 시간',
      card: 'line',
      cell: (a) => (
        <span className="tabular-nums">
          {fmtPer(a.views_per_user)} <span className="text-muted-foreground">·</span> {fmtDuration(a.avg_session_ms)}
        </span>
      ),
    },
    {
      key: 'more',
      header: '국가와 화면',
      card: 'actions',
      cell: (a) => {
        const on = open.has(a.app_id)
        return (
          <Button size="sm" variant="ghost" icon={on ? ChevronDown : ChevronRight} aria-expanded={on} onClick={() => toggle(a.app_id)}>
            {on ? '접기' : `국가·화면 ${countriesOf(a).length}곳`}
          </Button>
        )
      },
    },
  ]
  return (
    <DataTable
      label="앱·국가별 화면"
      mode="cards"
      rows={apps}
      rowKey={(a) => a.app_id}
      columns={columns}
      expand={{
        isOpen: (a) => open.has(a.app_id),
        render: (a) => (
          <div className="space-y-3">
            {sortCountries(countriesOf(a), sort).map((c) => (
              <div key={c.country} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2 font-medium">
                  <span>{countryName(c.country)}</span>
                  <span className="text-meta font-normal tabular-nums text-muted-foreground">
                    사용자 {fmtNum(c.users)} · 조회 {fmtNum(c.views)} · {fmtDuration(c.avg_session_ms)}
                  </span>
                </div>
                <ul className="space-y-0.5 border-l border-border pl-3">
                  {sortScreens(screensOf(c), sort).slice(0, SCREEN_LIMIT).map((s) => (
                    <li key={`${s.screen}|${s.app_version}`} className="flex items-baseline justify-between gap-2 text-table">
                      <span className="min-w-0 truncate">{s.screen || '(지정 안 됨)'}</span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        조회 {fmtNum(s.views)} · {fmtDuration(s.avg_dwell_ms)}
                      </span>
                    </li>
                  ))}
                </ul>
                {screensOf(c).length > SCREEN_LIMIT && (
                  <p className="pl-3 text-meta text-muted-foreground">위 {SCREEN_LIMIT}개만 보인다. 전체는 넓은 화면에서 본다.</p>
                )}
              </div>
            ))}
          </div>
        ),
      }}
    />
  )
}

/* The three levels sort by the same column but read different fields for time,
 * and only the app level has a name. The comparators live together so the
 * levels cannot drift apart. Each child list is read through a guard: the
 * server sends [] for a level with nothing under it, and a table is not worth
 * a blank page if that ever regresses. */

function countriesOf(a: AnalyticsUsageApp): AnalyticsUsageCountry[] {
  return a.countries ?? []
}

function screensOf(c: AnalyticsUsageCountry): AnalyticsUsageScreen[] {
  return c.screens ?? []
}

function measure(row: { users: number; views: number; views_per_user: number }, key: SortKey) {
  switch (key) {
    case 'users':
      return row.users
    case 'per':
      return row.views_per_user
    case 'views':
      return row.views
    default:
      return 0
  }
}

function sortApps(apps: AnalyticsUsageApp[], sort: Sort): AnalyticsUsageApp[] {
  const sign = sort.dir === 'desc' ? -1 : 1
  const name = (a: AnalyticsUsageApp) => a.linked?.name ?? a.app_id
  return [...apps].sort((a, b) => {
    let d = 0
    if (sort.key === 'name') d = name(a).localeCompare(name(b))
    else if (sort.key === 'time') d = a.avg_session_ms - b.avg_session_ms
    else d = measure(a, sort.key) - measure(b, sort.key)
    // A stable tiebreak, so equal rows keep one order instead of shuffling
    // whenever the query refetches.
    if (d === 0) d = name(a).localeCompare(name(b))
    return d * sign
  })
}

function sortCountries(rows: AnalyticsUsageCountry[], sort: Sort): AnalyticsUsageCountry[] {
  const sign = sort.dir === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => {
    // A country row has no name column of its own, so that column leaves these
    // in busiest-first order rather than pretending to sort by a value they
    // do not carry.
    if (sort.key === 'name') return b.views - a.views
    let d = sort.key === 'time' ? a.avg_session_ms - b.avg_session_ms : measure(a, sort.key) - measure(b, sort.key)
    if (d === 0) d = a.country.localeCompare(b.country)
    return d * sign
  })
}

function sortScreens(rows: AnalyticsUsageScreen[], sort: Sort): AnalyticsUsageScreen[] {
  const sign = sort.dir === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => {
    if (sort.key === 'name') return b.views - a.views
    let d = sort.key === 'time' ? a.avg_dwell_ms - b.avg_dwell_ms : measure(a, sort.key) - measure(b, sort.key)
    if (d === 0) d = a.screen.localeCompare(b.screen)
    return d * sign
  })
}

function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return <th scope="col" className={cn('h-9 whitespace-nowrap px-3 text-left font-medium', className)}>{children}</th>
}

function Td({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  return (
    <td colSpan={colSpan} className={cn('px-3 py-2', className)}>
      {children}
    </td>
  )
}

/** ISO-3166 alpha-2 to a display name, using the browser tables rather than a
 *  hand-kept list. Falls back to the code when the runtime has no name. */
function countryName(code: string): string {
  if (!code) return '—'
  try {
    return new Intl.DisplayNames(['ko'], { type: 'region' }).of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}
